import { useNotifier } from '@/lib/notifier'
import { appState } from '@/store/appState'
import { dirDuration } from '@/types/structs'
import { valueOrDefault } from '@/lib/format'
import type { DirectionFor } from '@/types/enums'
import walkPng from '@/assets/images/walk.png'
import bicyclePng from '@/assets/images/bicycle.png'
import carPng from '@/assets/images/car.png'

/* Port of GoogleMapSelectModeWidget — the walk/bicycle/car segmented switch.
 *
 * Track: width 100%, height 44, #F0F0F0, radius 90. Three equal segments
 * separated by 10px gaps, 10px lead/trail. A segment is a PLAIN container
 * (no transition animation — the animated version exists only on the dead
 * /directions mock): white when selected, transparent otherwise, radius 90,
 * inner padding 6, icon + gap + AutoSizeText.
 *
 * Segment quirks kept: walk's icon-text gap is 8 (others 10); bicycle.png
 * has width 24 but NO height (natural aspect 70x49 -> ~16.8px tall).
 *
 * Tap: only when different — silent write + update() (PUBLISHES, unlike the
 * sheet's view switches). Label: valueOrDefault(duration.text, '-'),
 * PoppinsRegular 12/1.1 black, 2px top pad, shrink-to-fit min 10px. */

const SEGMENTS: Array<{
  mode: DirectionFor
  icon: string
  gap: number
  iconStyle: React.CSSProperties
}> = [
  { mode: 'walk', icon: walkPng, gap: 8, iconStyle: { width: 24, height: 24, objectFit: 'cover' } },
  { mode: 'bicycle', icon: bicyclePng, gap: 10, iconStyle: { width: 24 } },
  { mode: 'car', icon: carPng, gap: 10, iconStyle: { width: 24, height: 24, objectFit: 'cover' } },
]

export function GoogleMapSelectMode() {
  const selected = useNotifier(appState, (s) => s.selectedDirectionFor)
  const durations: Record<DirectionFor, string> = {
    walk: dirDuration(appState.walkingDirectionModel).text,
    bicycle: dirDuration(appState.bicyclingDirectionModel).text,
    car: dirDuration(appState.drivingDirectionModel).text,
  }

  return (
    <div
      data-select-mode
      style={{
        width: '100%',
        height: 44,
        background: 'var(--segment-track)',
        borderRadius: 90,
        display: 'flex',
        alignItems: 'stretch',
        gap: 10,
        padding: '0 10px',
      }}
    >
      {SEGMENTS.map(({ mode, icon, gap, iconStyle }) => (
        <button
          key={mode}
          type="button"
          data-segment={mode}
          data-selected={selected === mode}
          onClick={() => {
            if (appState.selectedDirectionFor !== mode) {
              appState.selectedDirectionFor = mode
              appState.update() // publishes — page & map see it
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap,
            background: selected === mode ? '#ffffff' : 'var(--segment-inactive)',
            borderRadius: 90,
            padding: '0 6px',
            cursor: 'pointer',
          }}
        >
          <img src={icon} alt={mode} style={iconStyle} />
          <span
            data-duration-label
            style={{
              flex: 1,
              minWidth: 0,
              paddingTop: 2,
              fontFamily: 'var(--font-poppins-regular)',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 1.1,
              color: '#000000',
              textAlign: 'left',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {valueOrDefault(durations[mode], '-')}
          </span>
        </button>
      ))}
    </div>
  )
}
